import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../../pages/Home/home.module.css";
import IconButton from "common/IconButton";

const DateFilterDropdown = ({ onDateRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState("Last 7 Days");
  const [customStartDate, setCustomStartDate] = useState(new Date());
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [comparePeriod, setComparePeriod] = useState("Previous Period");
  const dropdownRef = useRef(null);

  const dateRanges = [
    { label: "Today", days: 0 },
    { label: "Yesterday", days: 1 },
    { label: "Last 7 Days", days: 7 },
    { label: "Last Week", type: "calendar", period: "week" },
    { label: "Last 30 Days", days: 30 },
    { label: "Last Month", type: "calendar", period: "month" },
    { label: "Last Year", type: "calendar", period: "year" },
    { label: "Year to Date", type: "ytd" },
    { label: "Custom", type: "custom" },
  ];
  
  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleRangeSelect = (range) => {
    setSelectedRange(range);
    if (range === "Custom") {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  // Helper function to get start of week (Sunday)
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday
    d.setDate(d.getDate() - day);
    return d;
  };

  // Helper function to get end of week (Saturday)
  const getEndOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sunday
    d.setDate(d.getDate() + (6 - day));
    return d;
  };

  // Helper function to get start of month
  const getStartOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  // Helper function to get end of month
  const getEndOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  // Helper function to get start of year
  const getStartOfYear = (date) => {
    return new Date(date.getFullYear(), 0, 1);
  };

  // Helper function to get end of year
  const getEndOfYear = (date) => {
    return new Date(date.getFullYear(), 11, 31);
  };

  const handleSave = () => {
    let startDate, endDate, days;
    const today = new Date();
    const range = dateRanges.find(r => r.label === selectedRange);

    if (!range) return;

    if (selectedRange === "Custom") {
      startDate = customStartDate;
      endDate = customEndDate;
      days = calculateDaysBetween(startDate, endDate);
    } else if (range.type === "calendar") {
      endDate = new Date();
      
      if (range.period === "week") {
        // Last week = previous full Sunday to Saturday
        const currentWeekStart = getStartOfWeek(today);
        endDate = new Date(currentWeekStart);
        endDate.setDate(endDate.getDate() - 1); // End of previous week (Saturday)
        startDate = getStartOfWeek(endDate); // Start of previous week (Sunday)
      } else if (range.period === "month") {
        // Last month = previous full calendar month
        endDate = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
        startDate = new Date(endDate);
        startDate.setDate(1); // First day of previous month
      } else if (range.period === "year") {
        // Last year = previous full calendar year
        endDate = new Date(today.getFullYear() - 1, 11, 31); // Dec 31 of previous year
        startDate = new Date(today.getFullYear() - 1, 0, 1); // Jan 1 of previous year
      }
      days = calculateDaysBetween(startDate, endDate);
    } else if (range.type === "ytd") {
      // Year to date = Jan 1 of current year to today
      startDate = new Date(today.getFullYear(), 0, 1);
      endDate = today;
      days = calculateDaysBetween(startDate, endDate);
    } else {
      endDate = new Date();
      
      if (range.label === "Today") {
        startDate = new Date();
      } else if (range.label === "Yesterday") {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 1);
        endDate.setDate(endDate.getDate() - 1);
      } else if (range.days) {
        startDate = new Date();
        startDate.setDate(startDate.getDate() - (range.days - 1));
      }
      
      days = range.days || 1;
    }
    
    // Calculate comparison period based on selection
    onDateRangeChange(startDate, endDate, days, comparePeriod);
    
    // Close the dropdown
    setIsOpen(false);
  };

  const calculateDaysBetween = (start, end) => {
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1; // Include both start and end days
  };

  const generateComparePeriods = (startDate, endDate) => {
    if (!startDate || !endDate) return [];
  
    const days = calculateDaysBetween(startDate, endDate);
    const range = dateRanges.find(r => r.label === selectedRange);
    
    // Calculate previous period dates (same length, just before)
    const previousPeriodStart = new Date(startDate);
    const previousPeriodEnd = new Date(endDate);
    previousPeriodStart.setDate(previousPeriodStart.getDate() - days);
    previousPeriodEnd.setDate(previousPeriodEnd.getDate() - days);
    
    // Calculate previous year period
    const previousYearStart = new Date(startDate);
    const previousYearEnd = new Date(endDate);
    previousYearStart.setFullYear(previousYearStart.getFullYear() - 1);
    previousYearEnd.setFullYear(previousYearEnd.getFullYear() - 1);
  
    // Format dates for display
    const format = (date) =>
      date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      });
    
    const options = [
      {
        label: `Previous Period (${format(previousPeriodStart)} - ${format(previousPeriodEnd)})`,
        value: "Previous Period"
      },
      {
        label: `Previous Year (${format(previousYearStart)} - ${format(previousYearEnd)})`,
        value: "Previous Year"
      }
    ];
    
    // For calendar-based periods, add specific comparison
    // if (range && range.type === "calendar") {
    //   let comparisonLabel = "";
    //   if (range.period === "week") {
    //     comparisonLabel = "Previous Week";
    //   } else if (range.period === "month") {
    //     comparisonLabel = "Previous Month";
    //   } else if (range.period === "year") {
    //     comparisonLabel = "Previous Year";
    //   }
      
    //   if (comparisonLabel) {
    //     options.unshift({
    //       label: comparisonLabel,
    //       value: comparisonLabel
    //     });
    //   }
    // }
    
    return options;
  };
  
  // Determine current date range for comparison periods
  let currentStartDate, currentEndDate;
  
  if (selectedRange === "Custom") {
    currentStartDate = customStartDate;
    currentEndDate = customEndDate;
  } else {
    const range = dateRanges.find(r => r.label === selectedRange);
    const today = new Date();
    
    if (range && range.type === "calendar") {
      if (range.period === "week") {
        const currentWeekStart = getStartOfWeek(today);
        const prevWeekEnd = new Date(currentWeekStart);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - 1);
        currentStartDate = getStartOfWeek(prevWeekEnd);
        currentEndDate = prevWeekEnd;
      } else if (range.period === "month") {
        currentEndDate = new Date(today.getFullYear(), today.getMonth(), 0);
        currentStartDate = new Date(currentEndDate);
        currentStartDate.setDate(1);
      } else if (range.period === "year") {
        currentEndDate = new Date(today.getFullYear() - 1, 11, 31);
        currentStartDate = new Date(today.getFullYear() - 1, 0, 1);
      }
    } else if (range && range.type === "ytd") {
      currentStartDate = new Date(today.getFullYear(), 0, 1);
      currentEndDate = today;
    } else {
      currentEndDate = new Date();
      
      if (range && range.label === "Today") {
        currentStartDate = new Date();
      } else if (range && range.label === "Yesterday") {
        currentStartDate = new Date();
        currentStartDate.setDate(currentStartDate.getDate() - 1);
        currentEndDate.setDate(currentEndDate.getDate() - 1);
      } else if (range && range.days) {
        currentStartDate = new Date();
        currentStartDate.setDate(currentStartDate.getDate() - (range.days - 1));
      } else {
        currentStartDate = new Date();
        currentStartDate.setDate(currentStartDate.getDate() - 6); // Default to Last 7 Days
      }
    }
  }
  
  let comparisonDates = currentStartDate && currentEndDate ? 
    generateComparePeriods(currentStartDate, currentEndDate) : 
    generateComparePeriods(new Date(new Date().setDate(new Date().getDate() - 6)), new Date());

  return (
    <div className={styles.dateFilterContainer} ref={dropdownRef}>
      <IconButton
        label={selectedRange}
        icon={"calendar"}
        buttonFunction={() => setIsOpen(!isOpen)}
      />
      
      {isOpen && (
        <div className={styles.dateFilterDropdown}>
          {!showCustomCalendar ? (
            <>
              {dateRanges.map((range) => (
                <div
                  key={range.label}
                  className={`${styles.dateFilterOption} ${selectedRange === range.label ? styles.selected : ""}`}
                  onClick={() => handleRangeSelect(range.label)}
                >
                  {range.label}
                </div>
              ))}
              
              {selectedRange !== "Custom" && (
                <div className={styles.compareSection}>
                  <div className={styles.compareDivider}>
                    <hr />
                    <span>Compare to</span>
                    <hr />
                  </div>
                  
                  <div className={styles.comparePeriodSelect}>
                    <select
                      value={comparePeriod}
                      onChange={(e) => setComparePeriod(e.target.value)}
                      className={styles.compareDropdown}
                    >
                      {comparisonDates.map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <button 
                    className={styles.applyButton}
                    onClick={handleSave}
                  >
                    Save
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className={styles.customDateContainer}>
              <div className={styles.customDateHeader}>
                <button onClick={() => setShowCustomCalendar(false)}>
                  &larr; Back
                </button>
                <h4>Custom Range</h4>
              </div>
              
              <div className={styles.customDatePickers}>
                <div>
                  <label>Start Date</label>
                  <DatePicker
                    selected={customStartDate}
                    onChange={date => setCustomStartDate(date)}
                    selectsStart
                    startDate={customStartDate}
                    endDate={customEndDate}
                    maxDate={new Date()}
                    popperContainer={({ children }) => (
                      <div className={styles.calendarPortalContainer}>{children}</div>
                    )}
                    popperClassName={styles.datePickerPopper}
                    popperPlacement="bottom-start"
                    withPortal
                  />
                </div>
                <div>
                  <label>End Date</label>
                  <DatePicker
                    selected={customEndDate}
                    onChange={date => setCustomEndDate(date)}
                    selectsEnd
                    startDate={customStartDate}
                    endDate={customEndDate}
                    minDate={customStartDate}
                    maxDate={new Date()}
                    popperClassName={styles.datePickerPopper}
                    popperPlacement="bottom-start"
                    withPortal
                  />
                </div>
              </div>
              
              <div className={styles.customDateFooter}>
                <div className={styles.dayCount}>
                  {calculateDaysBetween(customStartDate, customEndDate)} days
                </div>
                {/* <div className={styles.compareSection}>
                  <div className={styles.compareDivider}>
                    <span>Compare to</span>
                  </div>
                  
                  <div className={styles.comparePeriodSelect}>
                    <select
                      value={comparePeriod}
                      onChange={(e) => setComparePeriod(e.target.value)}
                      className={styles.compareDropdown}
                    >
                      {generateComparePeriods(customStartDate, customEndDate).map((period) => (
                        <option key={period.value} value={period.value}>
                          {period.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div> */}
                <button 
                  className={styles.applyButton}
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateFilterDropdown;